import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import Cookies from 'js-cookie'

type InternationalizationContextProps = {
  language: 'pt_BR' | 'en_US'
  handleChangeLanguage: (
    language: InternationalizationContextProps['language'],
  ) => void
}

const ONE_YEAR = 365 * 24 * 60 * 60 * 1000

const InternationalizationContext =
  createContext<InternationalizationContextProps>({
    language: 'pt_BR',
    handleChangeLanguage: () => {},
  })

export const InternationalizationProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const [language, setLanguage] =
    useState<InternationalizationContextProps['language']>('pt_BR')

  const handleUpdateLanguageCookie = useCallback((languageValue: string) => {
    Cookies.set('language', languageValue, {
      path: '/',
      expires: new Date(Date.now() + ONE_YEAR),
    })
  }, [])

  const handleChangeLanguage = useCallback(
    (languageValue: InternationalizationContextProps['language']) => {
      setLanguage(languageValue)
      handleUpdateLanguageCookie(languageValue)
    },
    [handleUpdateLanguageCookie],
  )

  useEffect(() => {
    const cookieLanguage = Cookies.get('language')

    if (cookieLanguage)
      handleChangeLanguage(
        cookieLanguage as InternationalizationContextProps['language'],
      )
  }, [handleChangeLanguage])

  const providerProps = useMemo(
    () => ({
      language,
      handleChangeLanguage,
    }),
    [language, handleChangeLanguage],
  )

  return (
    <InternationalizationContext.Provider value={providerProps}>
      {children}
    </InternationalizationContext.Provider>
  )
}

export const useInternationalization = () => {
  const context = useContext(InternationalizationContext)

  if (!context) {
    throw new Error(
      'useInternationalization must be used within a InternationalizationProvider',
    )
  }

  return context
}
